/**
 * Button primitive. Variants (primary/ghost/danger), sizes, and loading/disabled
 * are all props (OCP — a new look is a new variant value, never a fork). Fully
 * token-driven, focus-visible ring, reduced-motion honored. Emits `onClick`;
 * holds no domain logic.
 */
import type { JSX } from 'solid-js';
import { Show, splitProps } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';
import { Spinner } from './Spinner.tsx';

export type ButtonVariant = 'primary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const STYLE_ID = 'f4-button';
const CSS = `
.f4-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  font: inherit;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  user-select: none;
  transition: background-color var(--duration-fast, 150ms) var(--ease-standard, ease),
    box-shadow var(--duration-fast, 150ms) var(--ease-standard, ease),
    color var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-btn[disabled], .f4-btn[aria-busy="true"] {
  opacity: 0.6;
  cursor: not-allowed;
}
.f4-btn--full { width: 100%; }
.f4-btn--sm { padding: var(--space-1) var(--space-3); font-size: var(--font-size-sm); }
.f4-btn--md { padding: var(--space-2) var(--space-4); font-size: var(--font-size-md); }
.f4-btn--lg { padding: var(--space-3) var(--space-5); font-size: var(--font-size-lg); }
.f4-btn--primary { background: var(--color-accent); color: var(--color-text-on-accent); }
.f4-btn--primary:hover:not([disabled]):not([aria-busy="true"]) { background: var(--color-accent-hover); }
.f4-btn--ghost { background: transparent; color: var(--color-text); border-color: var(--color-border); }
.f4-btn--ghost:hover:not([disabled]):not([aria-busy="true"]) { background: var(--color-surface-sunken); }
.f4-btn--danger { background: var(--color-danger); color: var(--color-danger-contrast); }
.f4-btn--danger:hover:not([disabled]):not([aria-busy="true"]) { background: var(--color-accent-active); }
@media (prefers-reduced-motion: reduce) { .f4-btn { transition: none; } }
`;

export interface ButtonProps extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** Visual intent. Defaults to `primary`. */
  readonly variant?: ButtonVariant;
  /** Size preset. Defaults to `md`. */
  readonly size?: ButtonSize;
  /** Show a spinner and block interaction. */
  readonly loading?: boolean;
  /** Stretch to the container width. */
  readonly fullWidth?: boolean;
  /** Native button type. Defaults to `button` (never accidentally submits). */
  readonly type?: 'button' | 'submit' | 'reset';
  /** Accessible label for the loading spinner. Defaults to `Loading`. */
  readonly loadingLabel?: string;
}

export function Button(props: ButtonProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  const [local, rest] = splitProps(props, [
    'variant',
    'size',
    'loading',
    'fullWidth',
    'type',
    'loadingLabel',
    'class',
    'children',
    'disabled',
  ]);

  const isBusy = (): boolean => local.loading === true;

  return (
    <button
      {...rest}
      type={local.type ?? 'button'}
      class={cx(
        'f4-btn',
        'f4-focus-ring',
        `f4-btn--${local.variant ?? 'primary'}`,
        `f4-btn--${local.size ?? 'md'}`,
        local.fullWidth && 'f4-btn--full',
        local.class,
      )}
      disabled={local.disabled || isBusy()}
      aria-busy={isBusy() ? 'true' : undefined}
    >
      <Show when={isBusy()}>
        <Spinner size="sm" label={local.loadingLabel ?? 'Loading'} />
      </Show>
      {local.children}
    </button>
  );
}
